<template>
  <hr-content-card
    class="addendum-card"
    :class="`addendum-card--${isPending ? 'pending' : 'complete'}`"
  >
    <div class="addendum-card__header">
      <div>
        <div class="text-weight-medium">
          {{ title }}
        </div>
        <div class="text-caption">
          {{ sourceLabel }}
        </div>
        <div class="text-caption">
          {{ formatDate(addendum.createTimestamp) }}
        </div>
      </div>
      <hr-overflow-menu-btn
        v-if="canDeleteAddendum"
        :options="menuOptions"
        @click.stop
      />
    </div>

    <div class="addendum-card__fields">
      <div
        v-for="changedField in changedFieldRows"
        :key="changedField.key"
        class="row q-py-sm"
      >
        <div class="addendum-card__field-label text-weight-medium">
          {{ changedField.label }}
        </div>
        <div class="col">
          {{ changedField.value }}
        </div>
      </div>
    </div>

    <session-activity-signatures
      :signatures="signatures"
      :required-signatories="requiredSignatories"
      :allow-delete-signatures="false"
      :cancel-request-confirm-message="cancelRequestConfirmMessage"
      class="q-mt-md"
      @sign-request="$emit('sign-request', $event)"
      @cancel-signature-request="$emit('cancel-signature-request', $event)"
    />

    <template
      v-if="canManageSignatures"
      #actions
    >
      <hr-subtle-btn
        icon="person_add_alt"
        :label="$t('signature.btnRequestSignature')"
        @click="$emit('request-signature')"
      />
      <hr-supporting-btn
        v-if="isSignedByUser"
        icon="add"
        :label="$t('signature.btnAddSignature')"
        @click="$emit('add-signature')"
      />
      <hr-supporting-btn
        v-if="!hasUserSignatureRequest && !isSignedByUser"
        icon="fas fa-file-signature"
        :label="$q.screen.gt.sm ? $t('signature.btnSignNow') : null"
        @click="$emit('sign-now')"
      />
    </template>
  </hr-content-card>
</template>

<script>
import SessionActivitySignatures from 'components/SessionActivities/SessionActivitySignatures';
import { chain, get, isDate } from 'lodash';
import { mapState } from 'pinia';
import { ADDENDUM_STATUSES, ADDENDUM_TRACKED_APPOINTMENT_FIELDS } from 'src/constants/sessionActivities';
import { HrContentCard, HrOverflowMenuBtn, HrSubtleBtn, HrSupportingBtn } from 'src/design-system';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';

export default {
  name: 'SessionActivityAddendum',

  components: {
    SessionActivitySignatures,
    HrContentCard,
    HrOverflowMenuBtn,
    HrSubtleBtn,
    HrSupportingBtn,
  },

  mixins: [Auth, Interactions],

  props: {
    addendum: {
      type: Object,
      required: true,
    },

    sessionActivity: {
      type: Object,
      required: true,
    },

    disableActions: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'request-signature',
    'add-signature',
    'sign-now',
    'sign-request',
    'cancel-signature-request',
    'delete',
  ],

  computed: {
    ...mapState(auth, ['userDetails', 'isParent']),

    isPending() {
      return this.addendum.status === ADDENDUM_STATUSES.PENDING_SIGNATURES;
    },

    title() {
      return this.$t('addendums.title', { status: this.$t(`addendums.statuses.${this.addendum.status}`) });
    },

    sourceLabel() {
      return this.$t(`addendums.sources.${this.addendum.source}`);
    },

    signatures() {
      return get(this.addendum, 'signatures', []);
    },

    requiredSignatories() {
      return get(this.addendum, 'requiredSignatories', []);
    },

    /**
     * Cancelling the only pending request on an addendum nobody signed removes
     * the addendum, so that case warns instead of using the generic copy.
     */
    cancelRequestConfirmMessage() {
      const removesAddendum = !this.signatures.length && this.requiredSignatories.length === 1;

      return removesAddendum ? this.$t('addendums.cancelRequest.confirmRemovesAddendum') : null;
    },

    changedFieldRows() {
      const changedFields = this.addendum.changedFields || {};

      return ADDENDUM_TRACKED_APPOINTMENT_FIELDS
        .filter((field) => field in changedFields)
        .map((field) => ({
          key: field,
          label: this.$t(`auditLogReports.fields.${field}`),
          value: changedFields[field] ?? '—',
        }));
    },

    canManageSignatures() {
      return !this.disableActions && !this.isParent;
    },

    isSignedByUser() {
      return !(chain(this.signatures)
        .find({ userId: this.userDetails.userId })
        .isEmpty()
        .value());
    },

    hasUserSignatureRequest() {
      return !(chain(this.requiredSignatories)
        .find({ userId: this.userDetails.userId })
        .isEmpty()
        .value());
    },

    canDeleteAddendum() {
      return !this.disableActions
        && !this.signatures.length
        && this.userHasPrivilege('sessionActivities/delete');
    },

    menuOptions() {
      return [{
        label: this.$t('common.buttonDelete'),
        icon: 'delete',
        destructive: true,
        action: this.onDelete,
      }];
    },
  },

  methods: {

    onDelete() {
      this.confirmDialog(
        this.$t('addendums.delete.confirmTitle'),
        this.$t('addendums.delete.confirmMessage'),
        () => {
          this.$emit('delete');
        },
      );
    }, // onDelete()

    formatDate(date) {
      if (!date) {
        return '';
      }

      const inputDate = isDate(date) ? date : date.toDate();

      return inputDate.toLocaleDateString(undefined, { hour: 'numeric', minute: '2-digit' });
    }, // formatDate()

  },
};
</script>

<style lang="scss" scoped>
.addendum-card {
  // Spans the full card width, so it cancels HrContentCard's 16px body padding.
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin: -16px -16px 16px;
    padding: 16px;
  }

  &__fields {
    padding: 8px 16px;
    border: 1px solid var(--app-outline-variant, rgba(0, 0, 0, 0.12));
    border-radius: 2px;
  }

  // Keeps the value next to its label instead of a grid column's width away.
  &__field-label {
    min-width: 140px;
  }

  // Same colors as the session-level addendum banner; the SCSS fallback applies without a theme.
  &--pending &__header {
    background: var(--app-warning-container, #{$warning-bg-light});
  }

  &--complete &__header {
    background: var(--app-primary-container, #{$info-bg-light});
  }
}
</style>
